import * as React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Copy, Edit3, Info, Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableFilterTrigger,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { usePublishersQuery, usePublisherStatusCountsQuery } from '../../hooks';
import {
  isPublisherSortProperty,
  publisherPageSizes,
  PublisherSortDirection,
  PublisherSortProperty,
  PublisherStatus,
  resolvePublisherCreatedBy,
  resolvePublisherStatusLabel,
  resolvePublisherStatusTone,
  type Publisher,
} from '../../model';
import { usePublishersRouteFilters } from '../../usePublishersRouteFilters';
import { PublisherPlacementsDialog } from '../PublisherPlacementsDialog/PublisherPlacementsDialog';
import { formatDisplayDate } from '../../../../../lib/format';
import { copyToClipboard } from '../../../../../lib/clipboard';

const statusOptions = Object.values(PublisherStatus);

export function PublishersTable() {
  const navigate = useNavigate();
  const { can } = useShigoPermissions();
  const canCreatePublisher = can(ShigoPermission.SupplyPublishersCreate);
  const canEditPublisher = can(ShigoPermission.SupplyPublishersEdit);
  const canViewPublisherPlacements = can(ShigoPermission.SupplyPublishersPlacementsRead);
  const { filter, setSearch, setStatuses, setPagination, setSort } = usePublishersRouteFilters();
  const query = usePublishersQuery(filter);
  const counts = usePublisherStatusCountsQuery(filter.searchTerm);
  const [placementsPublisher, setPlacementsPublisher] = React.useState<Publisher>();
  const [searchDraft, setSearchDraft] = React.useState({
    source: filter.searchTerm,
    value: filter.searchTerm,
  });
  const searchValue =
    searchDraft.source === filter.searchTerm ? searchDraft.value : filter.searchTerm;
  React.useEffect(() => {
    if (searchValue === filter.searchTerm) return;
    const timeoutId = window.setTimeout(() => {
      setSearch(searchValue);
    }, 650);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [filter.searchTerm, searchValue, setSearch]);
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<Publisher>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('publishers.name'),
          headerClassName: 'w-72 pl-4',
          className: 'w-72 pl-4 font-medium',
          value: (publisher) => (
            <Link
              className={dashboardTableTextActionClassName()}
              to="/supply/publishers/$publisherId/details"
              params={{ publisherId: publisher.id }}
              search={{}}
            >
              {publisher.name}
            </Link>
          ),
          sortable: true,
          sortKey: PublisherSortProperty.Name,
        },
        {
          id: 'status',
          kind: DashboardDataTableColumnKind.Status,
          header: dashboardT('publishers.status'),
          label: (publisher) => resolvePublisherStatusLabel(publisher.status),
          tone: (publisher) => resolvePublisherStatusTone(publisher.status),
          sortable: true,
          sortKey: PublisherSortProperty.Status,
        },
        {
          id: 'apps',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('publishers.apps'),
          value: (publisher) => publisher.appsCnt ?? 0,
        },
        {
          id: 'domains',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('publishers.domains'),
          value: (publisher) => publisher.domainsCnt ?? 0,
        },
        {
          id: 'placements',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('publishers.placements'),
          value: (publisher) => {
            const count = publisher.placementsIds?.length ?? 0;
            return (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!count || !canViewPublisherPlacements}
                aria-label={dashboardT('publishers.placementsFor', { name: publisher.name })}
                onClick={() => {
                  setPlacementsPublisher(publisher);
                }}
              >
                {count}
              </Button>
            );
          },
        },
        {
          id: 'mcm',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('publishers.mcm'),
          value: (publisher) =>
            dashboardT(publisher.mcmActive ? 'publishers.yes' : 'publishers.no'),
        },
        {
          id: 'debug',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('publishers.debug'),
          value: (publisher) => dashboardT(publisher.debug ? 'publishers.yes' : 'publishers.no'),
        },
        {
          id: 'whitelist',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('publishers.whitelist'),
          value: (publisher) => {
            const enabled = [
              publisher.whitelistAppsEnabled ? dashboardT('publishers.apps') : '',
              publisher.whitelistDomainsEnabled ? dashboardT('publishers.domains') : '',
            ].filter(Boolean);
            return enabled.join(', ');
          },
        },
        {
          id: 'network',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('publishers.network'),
          value: (publisher) => publisher.network?.name,
          sortable: true,
          sortKey: PublisherSortProperty.NetworkName,
        },
        {
          id: 'secondaryId',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('publishers.secondaryId'),
          headerClassName: 'w-24',
          className: 'w-24',
          value: (publisher) => {
            const secondaryId = publisher.secondaryId;
            return secondaryId ? (
              <div className="group flex min-w-0 items-center gap-1">
                <span className="truncate">{secondaryId}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 focus:opacity-100"
                      aria-label={dashboardT('publishers.copyId')}
                      onClick={() => {
                        void copyToClipboard(secondaryId, {
                          successMessage: dashboardT('publishers.idCopied'),
                          errorMessage: dashboardT('publishers.idCopyError'),
                        });
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{dashboardT('publishers.copyId')}</TooltipContent>
                </Tooltip>
              </div>
            ) : null;
          },
          sortable: true,
          sortKey: PublisherSortProperty.SecondaryId,
        },
        {
          id: 'createdBy',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('publishers.createdBy'),
          headerClassName: 'w-56',
          className: 'w-56 whitespace-nowrap',
          value: resolvePublisherCreatedBy,
        },
        {
          id: 'created',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('publishers.created'),
          headerClassName: 'w-28',
          className: 'w-28 whitespace-nowrap',
          value: (publisher) => formatDisplayDate(publisher.createdOn),
          sortable: true,
          sortKey: PublisherSortProperty.CreatedOn,
        },
        {
          id: 'updated',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('publishers.updated'),
          headerClassName: 'w-28',
          className: 'w-28 whitespace-nowrap',
          value: (publisher) => formatDisplayDate(publisher.updatedOn),
        },
        {
          id: 'notes',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('publishers.notes'),
          headerClassName: 'w-16 text-center',
          className: 'w-16 text-center',
          value: (publisher) =>
            publisher.notes ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label={dashboardT('publishers.viewNotes')}
                  >
                    <Info className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-80 whitespace-normal">
                  {publisher.notes}
                </TooltipContent>
              </Tooltip>
            ) : null,
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          cell: (publisher) =>
            canEditPublisher ? (
              <DashboardTableRowActions label={dashboardT('publishers.actions')}>
                <DashboardTableRowActionItem
                  onSelect={() => {
                    void navigate({
                      to: '/supply/publishers/edit/$publisherId',
                      params: { publisherId: publisher.id },
                    });
                  }}
                >
                  <Edit3 className="h-4 w-4" />
                  {dashboardT('publishers.edit')}
                </DashboardTableRowActionItem>
              </DashboardTableRowActions>
            ) : null,
        },
      ]),
    [canEditPublisher, canViewPublisherPlacements, navigate],
  );
  const selectedStatuses = new Set(filter.statuses);
  const statusLabel = filter.statuses.length
    ? filter.statuses.map(resolvePublisherStatusLabel).join(', ')
    : dashboardT('publishers.allStatuses');

  function updateStatus(status: PublisherStatus, checked: boolean) {
    setStatuses(
      checked
        ? Array.from(new Set([...filter.statuses, status]))
        : filter.statuses.filter((item) => item !== status),
    );
  }

  return (
    <>
      <DashboardDataTable
        title={dashboardT('publishers.title')}
        tableLabel={dashboardT('publishers.title')}
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(publisher) => publisher.id}
        loading={query.isLoading}
        loadingMessage={dashboardT('publishers.loading')}
        errorMessage={query.isError ? dashboardT('publishers.loadError') : undefined}
        emptyMessage={dashboardT('publishers.empty')}
        minWidthClassName="min-w-[1680px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label={dashboardT('publishers.search')}
              placeholder={dashboardT('publishers.search')}
              value={searchValue}
              onChange={(event) => {
                setSearchDraft({ source: filter.searchTerm, value: event.target.value });
              }}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <DashboardTableFilterTrigger
                  label={statusLabel}
                  aria-label={dashboardT('publishers.statusFilter')}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {statusOptions.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={selectedStatuses.has(status)}
                    onSelect={(event) => {
                      event.preventDefault();
                    }}
                    onCheckedChange={(checked) => {
                      updateStatus(status, checked);
                    }}
                  >
                    <span className="flex min-w-0 flex-1 justify-between gap-3">
                      <span>{resolvePublisherStatusLabel(status)}</span>
                      {counts.data ? (
                        <span className="text-muted-foreground">{counts.data[status] ?? 0}</span>
                      ) : null}
                    </span>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {canCreatePublisher ? (
              <Button asChild>
                <Link to="/supply/publishers/create">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {dashboardT('publishers.create')}
                </Link>
              </Button>
            ) : null}
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: publisherPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            setPagination(next.page, next.pageSize);
          },
        }}
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === PublisherSortDirection.Asc
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (next) => {
            if (!next) {
              setSort(null, filter.sortDirection);
              return;
            }
            if (!isPublisherSortProperty(next.key)) return;
            setSort(
              next.key,
              next.direction === DashboardDataTableSortDirection.Asc
                ? PublisherSortDirection.Asc
                : PublisherSortDirection.Desc,
            );
          },
        }}
      />
      {placementsPublisher ? (
        <PublisherPlacementsDialog
          key={placementsPublisher.id}
          publisher={placementsPublisher}
          onOpenChange={(open) => {
            if (!open) setPlacementsPublisher(undefined);
          }}
        />
      ) : null}
    </>
  );
}
